import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Calendar, Plus, Clock, CheckCircle, XCircle, Loader2,
  Palmtree, Thermometer, Baby, AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const ALL_LEAVE_TYPES = [
  { value: 'Annual Leave', label: 'Annual Leave', icon: Palmtree, color: 'bg-green-100 text-green-800', gender: 'all' },
  { value: 'Sick Leave', label: 'Sick Leave', icon: Thermometer, color: 'bg-red-100 text-red-800', gender: 'all' },
  { value: 'Maternity Leave', label: 'Maternity Leave', icon: Baby, color: 'bg-pink-100 text-pink-800', gender: 'female' },
  { value: 'Paternity Leave', label: 'Paternity Leave', icon: Baby, color: 'bg-blue-100 text-blue-800', gender: 'male' },
  { value: 'Unpaid Leave', label: 'Unpaid Leave', icon: AlertCircle, color: 'bg-gray-100 text-gray-800', gender: 'all' },
  { value: 'Other', label: 'Other', icon: AlertCircle, color: 'bg-gray-100 text-gray-800', gender: 'all' },
];

const MyLeave = () => {
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    leave_type: 'Annual Leave',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { getAuthHeaders } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Filter leave types based on gender
  const getAvailableLeaveTypes = () => {
    const gender = balance?.gender?.toLowerCase() || '';
    return ALL_LEAVE_TYPES.filter(lt => 
      lt.gender === 'all' || lt.gender === gender
    );
  };

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leave/my-requests`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setRequests(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/api/leave/my-balance`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setBalance(await response.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRequests(), fetchBalance()]);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/leave/request`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowModal(false);
        setFormData({ leave_type: 'Annual Leave', start_date: '', end_date: '', reason: '' });
        await fetchRequests();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to submit request');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await fetch(`${API_URL}/api/leave/request/${requestId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      await fetchRequests();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pending Manager': 'bg-orange-100 text-orange-800 border border-orange-300',
      'Pending HR': 'bg-amber-100 text-amber-800 border border-amber-300',
      'Approved': 'bg-green-100 text-green-800 border border-green-300',
      'Rejected': 'bg-red-100 text-red-800 border border-red-300',
      'Cancelled': 'bg-gray-100 text-gray-700 border border-gray-300',
      'Pending': 'bg-yellow-100 text-yellow-800 border border-yellow-300'
    };
    const icons = {
      'Pending Manager': Clock,
      'Pending HR': Clock,
      'Approved': CheckCircle,
      'Rejected': XCircle,
      'Cancelled': XCircle,
      'Pending': Clock
    };
    const Icon = icons[status] || Clock;
    return (
      <Badge className={`flex items-center gap-1 font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const getLeaveTypeInfo = (type) => {
    return ALL_LEAVE_TYPES.find(t => t.value === type) || ALL_LEAVE_TYPES[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const availableLeaveTypes = getAvailableLeaveTypes();

  return (
    <div className="space-y-6" data-testid="my-leave-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Leave</h1>
          <p className="text-gray-600">Manage your leave requests and view balance</p>
        </div>
        <Button onClick={() => setShowModal(true)} data-testid="request-leave-button">
          <Plus className="h-4 w-4 mr-2" />
          Request Leave
        </Button>
      </div>

      {/* Leave Balance */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-green-700">Annual Leave</p>
                <p className="text-2xl font-bold text-green-800">{balance?.annual_leave?.remaining || 0}</p>
                <p className="text-xs text-green-600">of {balance?.annual_leave?.total || 21} days</p>
              </div>
              <Palmtree className="h-8 w-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-700">Sick Leave</p>
                <p className="text-2xl font-bold text-red-800">{balance?.sick_leave?.remaining || 0}</p>
                <p className="text-xs text-red-600">of {balance?.sick_leave?.entitled || 10} days</p>
              </div>
              <Thermometer className="h-8 w-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
        {/* Maternity Leave - Only for female employees */}
        {balance?.maternity_leave && (
          <Card className="bg-pink-50 border-pink-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-pink-700">Maternity</p>
                  <p className="text-2xl font-bold text-pink-800">{balance?.maternity_leave?.remaining || 0}</p>
                  <p className="text-xs text-pink-600">of {balance?.maternity_leave?.entitled || 90} days</p>
                </div>
                <Baby className="h-8 w-8 text-pink-400" />
              </div>
            </CardContent>
          </Card>
        )}
        {/* Paternity Leave - Only for male employees */}
        {balance?.paternity_leave && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-700">Paternity</p>
                  <p className="text-2xl font-bold text-blue-800">{balance?.paternity_leave?.remaining || 0}</p>
                  <p className="text-xs text-blue-600">of {balance?.paternity_leave?.entitled || 14} days</p>
                </div>
                <Baby className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Carried Over Notice */}
      {balance?.annual_leave?.carried_over > 0 && (
        <Alert>
          <AlertDescription>
            You have {balance.annual_leave.carried_over} day(s) of annual leave carried over from the previous year.
          </AlertDescription>
        </Alert>
      )}

      {/* Leave Requests */}
      <Card>
        <CardHeader>
          <CardTitle>Leave Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {requests.length > 0 ? (
            <div className="space-y-4">
              {requests.map(request => {
                const typeInfo = getLeaveTypeInfo(request.leave_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div key={request.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${typeInfo.color.split(' ')[0]}`}>
                        <TypeIcon className={`h-5 w-5 ${typeInfo.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <p className="font-semibold">{request.leave_type}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(request.start_date).toLocaleDateString()} - {new Date(request.end_date).toLocaleDateString()}
                          <span className="ml-2">({request.days_requested} days)</span>
                        </p>
                        {request.reason && (
                          <p className="text-sm text-gray-600 mt-1">{request.reason}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(request.status)}
                      {(request.status === 'Pending Manager' || request.status === 'Pending HR' || request.status === 'Pending') && (
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleCancel(request.id)}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No leave requests</h3>
              <p className="text-gray-600 mb-4">You haven't submitted any leave requests yet</p>
              <Button onClick={() => setShowModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Request Leave
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="leave-request-modal">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
            
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={formData.leave_type} onValueChange={(v) => setFormData({...formData, leave_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableLeaveTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} required />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason (Optional)</Label>
              <Textarea value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} rows={3} placeholder="Brief description..." />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                Submit Request
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyLeave;
